// src/pages/Admin/PagesPersonnalisees/components/PageDetails.jsx

import React from "react";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaGlobe,
  FaHashtag,
  FaListAlt,
  FaPen,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import {
  formatDate,
  getFirstArticleImage,
  getMediaUrl,
  getMenuClass,
  getMenuLabel,
  getPageArticles,
  getPageDate,
  getStatusClass,
  getStatusLabel,
  truncateText,
} from "../utils/pagesPersonnaliseesUtils";

function DetailItem({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PageDetails({
  page,
  onEdit,
  onManageArticles,
  onClose,
}) {
  const articles = getPageArticles(page);
  const coverUrl = getMediaUrl(getFirstArticleImage(page));

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="relative min-h-[260px] bg-slate-100 dark:bg-slate-800">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={page.titre}
              className="h-[280px] w-full object-cover"
            />
          ) : (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-slate-400">
              <FaFileAlt className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucun visuel d’article</p>
            </div>
          )}

          <div className="absolute left-5 top-5 flex flex-wrap gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                page.actif
              )}`}
            >
              {getStatusLabel(page.actif)}
            </span>

            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${getMenuClass(
                page.afficher_menu
              )}`}
            >
              {getMenuLabel(page.afficher_menu)}
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-200">
              Page personnalisée
            </p>
            <h3 className="mt-1 text-3xl font-bold text-white">
              {page.titre}
            </h3>
            <p className="mt-2 text-sm font-bold text-cyan-100">
              /page/{page.slug}
            </p>
          </div>
        </div>

        {page.introduction && (
          <div className="p-5">
            <p className="whitespace-pre-line text-base font-semibold leading-7 text-slate-700 dark:text-slate-200">
              {page.introduction}
            </p>
          </div>
        )}
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Informations
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem label="Titre" value={page.titre} icon={FaFileAlt} />

          <DetailItem
            label="Slug"
            value={`/page/${page.slug}`}
            icon={FaGlobe}
          />

          <DetailItem
            label="Date de création"
            value={formatDate(getPageDate(page))}
            icon={FaCalendarAlt}
          />

          <DetailItem
            label="Ordre menu"
            value={String(page.ordre_menu ?? 0)}
            icon={FaHashtag}
          />

          <DetailItem
            label="Nombre d’articles"
            value={`${articles.length} article${articles.length > 1 ? "s" : ""}`}
            icon={FaListAlt}
          />

          <DetailItem
            label="Statut"
            value={getStatusLabel(page.actif)}
            icon={FaFileAlt}
          />
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          SEO
        </h4>

        <div className="grid gap-3">
          <DetailItem
            label="Meta title"
            value={page.meta_title}
            icon={FaSearch}
          />

          <DetailItem
            label="Meta description"
            value={page.meta_description}
            icon={FaSearch}
          />
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Aperçu des articles
        </h4>

        {articles.length ? (
          <div className="grid gap-3">
            {articles.slice(0, 5).map((article) => (
              <article
                key={article.id}
                className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
              >
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  {article.titre_article || "Article sans titre"}
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  {truncateText(article.contenu_texte, 180) ||
                    "Aucun texte renseigné."}
                </p>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
              Aucun article pour cette page.
            </p>
          </div>
        )}
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onManageArticles}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3 text-sm font-bold text-violet-700 transition hover:bg-violet-100 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/20"
          >
            <FaListAlt />
            Gérer les articles
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}